/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import { chartConfigUtilService, DonutDataItem, SpacingTuple } from './chartConfigUtilService';
import { GenericChart } from './HighchartsGeneric';

const { getResponsiveSpacing } = chartConfigUtilService;

// Stable default so the additionalConfig fallback doesn't create a new reference each render.
const EMPTY_ADDITIONAL_CONFIG: Highcharts.Options = {};

export interface DonutChartProps {
    /** Data items for the donut chart segments */
    data: DonutDataItem[];
    /** Main text displayed in the center of the donut (e.g., total value) */
    centerText: string;
    /** Sub text displayed below the main text (e.g., "Total Domains") */
    subText: string;
    /** Optional unit to display with values (e.g., "GB", "users") */
    unit?: string;
    /** Series name for tooltip */
    seriesName?: string;
    /** Chart height in pixels */
    height?: number;
    /** Override responsive spacing: [top, right, bottom, left] in pixels */
    spacing?: SpacingTuple;
    /** Additional Highcharts options to merge */
    additionalConfig?: Highcharts.Options;
    /** Callback fired when a label/segment is clicked, receives the item's name and urn */
    onPointClick?: (name: string, urn?: string) => void;
}

export const DonutChart: React.FC<DonutChartProps> = ({
    data,
    centerText,
    subText,
    unit = '',
    seriesName = 'Data',
    height = '100%',
    spacing,
    additionalConfig = EMPTY_ADDITIONAL_CONFIG,
    onPointClick,
}) => {
    const [responsiveSpacing, setResponsiveSpacing] = useState<SpacingTuple>(getResponsiveSpacing);

    useEffect(() => {
        const handleResize = () => setResponsiveSpacing(getResponsiveSpacing());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Stable config reference: only changes when the data/display inputs change, so
    // GenericChart recreates the chart once per real data change instead of every render.
    const chartConfig = useMemo(
        () =>
            chartConfigUtilService.createInteractiveDonutChartConfig(
                data,
                centerText,
                subText,
                unit,
                seriesName,
                { chart: { height }, ...additionalConfig },
                onPointClick,
                spacing ?? responsiveSpacing,
            ),
        [
            data,
            centerText,
            subText,
            unit,
            seriesName,
            height,
            additionalConfig,
            onPointClick,
            spacing,
            responsiveSpacing,
        ],
    );

    if (data.length === 0) return null;

    return <GenericChart config={chartConfig} />;
};

export default DonutChart;

// Re-export types for convenience
export type { DonutDataItem };
